"use client";

import { useState } from "react";
import { Mail, Loader2, RefreshCw } from "lucide-react";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { toast } from "sonner";
export function EmailVerificationBanner() {
  const {
    locale
  } = useTranslation();
  const {
    user,
    accessToken,
    setUser
  } = useAuthStore();
  const [isResending, setIsResending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  if (!user || user.email_verified) {
    return null;
  }
  const handleRefresh = async () => {
    if (!accessToken) return;
    setIsRefreshing(true);
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
      const res = await fetch(`${API_URL}/api/v1/users/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        if (userData.email_verified) {
          toast.success(locale === "fr" ? "Email vérifié !" : "Email verified!");
        } else {
          toast.info(locale === "fr" ? "Pas encore vérifié. Pense à cliquer sur le lien reçu." : "Not verified yet. Don't forget to click the link in the email.");
        }
      }
    } catch {
      toast.error(locale === "fr" ? "Erreur de connexion" : "Connection error");
    } finally {
      setIsRefreshing(false);
    }
  };
  const handleResend = async () => {
    setIsResending(true);
    try {
      const API_URL_0 = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
      const res_0 = await fetch(`${API_URL_0}/api/v1/auth/resend-verification?email=${encodeURIComponent(user.email)}`, {
        method: "POST"
      });
      const data = await res_0.json();
      if (res_0.ok) {
        if (data.message?.includes("already verified")) {
          toast.info(locale === "fr" ? "Email déjà vérifié ! Actualisation..." : "Email already verified! Refreshing…");
          handleRefresh();
        } else {
          toast.success(locale === "fr" ? "Email de vérification envoyé !" : "Verification email sent!");
        }
      } else {
        toast.error(locale === "fr" ? "Erreur lors de l'envoi" : "Failed to send the email");
      }
    } catch {
      toast.error(locale === "fr" ? "Erreur de connexion" : "Connection error");
    } finally {
      setIsResending(false);
    }
  };
  return <div className="bg-gradient-to-r from-yellow-500/95 to-yellow-600 text-white">
      <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <Mail className="h-4 w-4 shrink-0" />
          <span className="font-medium shrink-0">
            {locale === "fr" ? "Email non vérifié" : "Email not verified"}
          </span>
          <span className="hidden truncate sm:inline">
            {locale === "fr" ? `— Vérifie ${user.email} pour débloquer tout.` : `— Verify ${user.email} to unlock everything.`}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button onClick={handleResend} disabled={isResending} className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-white/25 disabled:opacity-60">
            {isResending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            {locale === "fr" ? "Renvoyer" : "Resend"}
          </button>
          <button onClick={handleRefresh} disabled={isRefreshing} className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-white/25 disabled:opacity-60">
            {isRefreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {locale === "fr" ? "J'ai vérifié" : "I verified"}
          </button>
        </div>
      </div>
    </div>;
}